import React from 'react';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import { formatSourceProvenance } from '../utils/provenance';

import { DetailSection, Field } from './EligibilityFinancialsBlock';
import { MemberIdField } from './MemberIdField';

/** The check's own alert: the payer lists another member's ID than ours. */
const MEMBER_ID_MISMATCH_ALERT = 'MEMBER_ID_MISMATCH';

interface Props {
  patientId?: string;
  encounterId?: string;
}

/**
 * Coverage block — plan + member-ID picture for this visit. Production
 * EligibilityCheckResponse exposes a leaner subset than the Nirvana
 * snapshot the prototype renders (no plan_name, plan_type, group_id at
 * this level), so we lead with what's reliable: coverage status, plan
 * status, and our member ID with any other ID the payer lists.
 */
export const EligibilityCoverageBlock: React.FC<Props> = ({ patientId, encounterId }) => {
  const { eligibilityData: data } = useEligibilityBadgeState({ patientId, encounterId });
  if (!data) return null;
  const provenance = formatSourceProvenance(data.source);

  const coverageStatus = humanize(data.coverage_status);
  const planStatus = humanize(data.plan_status);
  const payerName = data.payer_name?.trim();
  const memberId = data.submitted_member_id;
  const payerListedId = data.corrected_member_id;

  const hasAnything =
    payerName || coverageStatus || planStatus || memberId || payerListedId || data.service_tier;
  if (!hasAnything) return null;

  return (
    <DetailSection title="Coverage" provenance={provenance}>
      {payerName && <Field label="Payer" value={payerName} />}
      {coverageStatus && <Field label="Coverage" value={coverageStatus} />}
      {planStatus && <Field label="Plan status" value={planStatus} />}
      {data.service_tier && <Field label="Network" value={humanize(data.service_tier)} />}
      {(memberId || payerListedId) && (
        <MemberIdField
          memberId={memberId}
          payerListedId={payerListedId}
          anotherMember={data.alerts?.includes(MEMBER_ID_MISMATCH_ALERT) ?? false}
        />
      )}
    </DetailSection>
  );
};

/**
 * Best-effort enum → label conversion. Snake_case becomes Title Case;
 * already-readable strings pass through. Strict guard: anything outside the
 * snake_case shape is rendered verbatim (trust the BE if it sent prose).
 */
function humanize(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^[a-z][a-z0-9_]*$/.test(trimmed)) return trimmed;
  return trimmed
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
